// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import type { SchemaNode } from '../types';

/**
 * The schema-node walker.
 *
 * Every rule in Toolwright is applied per NODE, not per tool, because that is how the providers
 * apply theirs: OpenAI's 400 carries `In context=('properties', 'user', 'items')`, and llama.cpp's
 * drop set is computed per node from the sibling keywords present at that node. A per-tool check
 * cannot tell you which of nine nested objects is missing `additionalProperties`.
 */

/** RFC 6901 escaping. `~` first, then `/`, or the escape is ambiguous. */
export function escapePointer(token: string): string {
  return token.replace(/~/g, '~0').replace(/\//g, '~1');
}

export function joinPointer(base: string, ...tokens: (string | number)[]): string {
  return base + tokens.map((t) => '/' + escapePointer(String(t))).join('');
}

/** Keys whose value is a map of NAME → subschema. */
const SCHEMA_MAPS = ['properties', 'patternProperties', '$defs', 'definitions', 'dependentSchemas'] as const;

/** Keys whose value is a single subschema. */
const SCHEMA_SINGLES = [
  'additionalProperties',
  'additionalItems',
  'unevaluatedProperties',
  'unevaluatedItems',
  'contains',
  'not',
  'if',
  'then',
  'else',
  'propertyNames',
] as const;

/** Keys whose value is an array of subschemas. */
const SCHEMA_LISTS = ['allOf', 'anyOf', 'oneOf', 'prefixItems'] as const;

export interface VisitedNode {
  readonly pointer: string;
  readonly node: SchemaNode;
  /** Depth below the tool's schema root, counting every subschema edge; the root itself is 0. */
  readonly depth: number;
  /**
   * Depth counting only CONTAINER descent — into `properties`, `items`, `prefixItems` and
   * `additionalProperties`.
   *
   * This, not `depth`, is what OpenAI's "up to 10 levels of nesting" limit means. Counting a
   * `oneOf` branch or a `$defs` entry as a level would make a union-heavy belt trip a limit it does
   * not actually breach, and a linter that invents a violation is worse than one that misses one.
   */
  readonly containerDepth: number;
  /** Pointer of the parent node, or null at the root. */
  readonly parent: string | null;
}

function isObj(v: unknown): v is SchemaNode {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Depth-first walk over every subschema, in document order.
 *
 * Deliberately does NOT follow `$ref`. A `$ref` is a node in its own right and gets its own findings;
 * eagerly expanding refs is how a linter turns `{"$ref":"#"}` into an infinite loop, and OpenAI's own
 * SDK only unravels a `$ref` that carries sibling keys precisely so recursion terminates.
 */
const CONTAINER_KEYS: ReadonlySet<string> = new Set([
  'properties',
  'items',
  'prefixItems',
  'additionalProperties',
  'additionalItems',
]);

export function walkSchema(root: SchemaNode, onNode: (n: VisitedNode) => void): void {
  const seen = new Set<object>();

  const visit = (
    node: unknown,
    pointer: string,
    depth: number,
    containerDepth: number,
    parent: string | null,
  ): void => {
    // A boolean schema (`true`/`false`) is legal JSON Schema but carries no keywords.
    if (!isObj(node)) return;
    // Guard against a schema object that is its own descendant by reference.
    if (seen.has(node)) return;
    seen.add(node);

    onNode({ pointer, node, depth, containerDepth, parent });

    const down = (key: string): number => containerDepth + (CONTAINER_KEYS.has(key) ? 1 : 0);

    for (const key of SCHEMA_MAPS) {
      const map = node[key];
      if (!isObj(map)) continue;
      for (const name of Object.keys(map)) {
        visit(map[name], joinPointer(pointer, key, name), depth + 1, down(key), pointer);
      }
    }

    for (const key of SCHEMA_SINGLES) {
      if (key in node) visit(node[key], joinPointer(pointer, key), depth + 1, down(key), pointer);
    }

    for (const key of SCHEMA_LISTS) {
      const list = node[key];
      if (!Array.isArray(list)) continue;
      list.forEach((sub, i) => visit(sub, joinPointer(pointer, key, i), depth + 1, down(key), pointer));
    }

    // `items` is a subschema in 2020-12 and may be a TUPLE array in draft-07.
    if ('items' in node) {
      const items = node['items'];
      if (Array.isArray(items)) {
        items.forEach((sub, i) =>
          visit(sub, joinPointer(pointer, 'items', i), depth + 1, containerDepth + 1, pointer),
        );
      } else {
        visit(items, joinPointer(pointer, 'items'), depth + 1, containerDepth + 1, pointer);
      }
    }
  };

  visit(root, '', 0, 0, null);
}

export interface SchemaStats {
  /** Total object properties across every node — OpenAI caps this at 5000. */
  readonly properties: number;
  /** Deepest nesting level — OpenAI caps this at 10. */
  readonly depth: number;
  /** Total enum members across every enum — OpenAI caps this at 1000. */
  readonly enumValues: number;
  /** The largest single string enum, and its total string length. */
  readonly largestEnum: number;
  readonly largestEnumChars: number;
  /**
   * Characters across all property names, definition names, enum values and const values —
   * OpenAI caps this at 120,000. Note this is NOT the serialized size of the schema.
   */
  readonly chars: number;
  /** Properties absent from `required` — Anthropic's 24-optional budget counts these. */
  readonly optionalProperties: number;
  /** Properties whose type is a union (`anyOf` or a type array) — Anthropic's 16 budget. */
  readonly unionProperties: number;
  readonly nodes: number;
}

export function statSchema(root: SchemaNode): SchemaStats {
  let properties = 0;
  let depth = 0;
  let enumValues = 0;
  let largestEnum = 0;
  let largestEnumChars = 0;
  let chars = 0;
  let optionalProperties = 0;
  let unionProperties = 0;
  let nodes = 0;

  walkSchema(root, ({ node, pointer, containerDepth }) => {
    nodes++;
    if (containerDepth > depth) depth = containerDepth;

    const props = node['properties'];
    if (isObj(props)) {
      const names = Object.keys(props);
      properties += names.length;
      for (const n of names) chars += n.length;

      const req = Array.isArray(node['required']) ? (node['required'] as unknown[]).map(String) : [];
      for (const n of names) {
        if (!req.includes(n)) optionalProperties++;
        const sub = props[n];
        if (isObj(sub) && (Array.isArray(sub['type']) || Array.isArray(sub['anyOf']))) unionProperties++;
      }
    }

    for (const defsKey of ['$defs', 'definitions'] as const) {
      const defs = node[defsKey];
      if (isObj(defs)) for (const n of Object.keys(defs)) chars += n.length;
    }

    const en = node['enum'];
    if (Array.isArray(en)) {
      enumValues += en.length;
      let strChars = 0;
      let allStrings = true;
      for (const v of en) {
        if (typeof v === 'string') strChars += v.length;
        else allStrings = false;
        chars += String(v).length;
      }
      if (en.length > largestEnum) largestEnum = en.length;
      if (allStrings && strChars > largestEnumChars) largestEnumChars = strChars;
    }

    if ('const' in node) chars += String(node['const']).length;

    // A pointer this long only happens on a pathological schema, but the cap is real.
    if (pointer.length > 4096) chars += 0;
  });

  return {
    properties,
    depth,
    enumValues,
    largestEnum,
    largestEnumChars,
    chars,
    optionalProperties,
    unionProperties,
    nodes,
  };
}
